import { fromJS, List, Map } from 'immutable';
import { pathSubscription, regSub, subscribe, subscription } from 'nike-re-framejs';

import { BETSLIP_TYPES, isScoreboardError } from '@/common/client';
import { HOME } from '@/routing/routes';
import { STAKE_CURRENCY } from '@/betslip/money';
import { accountActionAllowed$ } from '@/account/subs';

const groupOrder = { T: 0, A: 1, B: 2, C: 3, D: 4, E: 5, F: 6, G: 7, H: 8, I: 9, J: 10 };
const stakeOrder = {
    Combination1: 1,
    Combination2: 2,
    Combination3: 3,
    Combination4: 4,
    Combination5: 5,
    Combination6: 6,
    Combination7: 7,
    Combination8: 8,
    Combination9: 9,
    Combination10: 10,
    Combination11: 11,
    Combination12: 12,
    Combination13: 13,
    Combination14: 14,
    Combination15: 15,
    Goliath: 11,
    Superman: 12
};

const serverBetslip = subscription(db => db.getIn(['server', 'betslip'], Map()));

export const serverBetslipAppliedExtra = subscription(bs => bs.getIn(['extra']), serverBetslip);
/** automatic odds advantage */
export const serverBetslipAoa = subscription(bs => bs.get('aoa'), serverBetslip);
export const serverBetslipAoaOdds = subscription(bs => bs.getIn(['aoa', 'oddsAdvantage']), serverBetslip);

function upstream() {
    return subscribe(['server/betslip']);
}
regSub('server/betslip', db => db.getIn(['server', 'betslip'], Map()));
regSub('betslip', db => db.getIn(['betslip']));
regSub('betslip/acceptChanges', upstream, bs => bs.getIn(['acceptChanges']));
regSub('betslip/availablePickGroups', upstream, bs => bs.getIn(['availableGroups']));
regSub('betslip/bonusGame', upstream, bs => bs.getIn(['bonusGame']));
regSub('betslip/combination', upstream, (bs, [, stakeType]) => bs.getIn(['combinations', stakeType], Map()));
regSub('betslip/currentPickIds', db => db.getIn(['server', 'currentPickIds'], List()));
regSub('betslip/enableSave', upstream, bs => bs.getIn(['enableSave']));
regSub('betslip/extraAvailable/advantage', upstream, bs => bs.getIn(['extraAdvantageInterval']));
regSub('betslip/extraAvailable/freeTicket', upstream, bs => bs.getIn(['extrasAvailable', 'FreeTicket']));
regSub('betslip/extraOverridesStake', upstream, bs => bs.getIn(['extraOverridesStake']));
regSub('betslip/gridCode', upstream, bs => bs.getIn(['submit', 'gridCode']));
regSub('betslip/group', upstream, (bs, [, groupId]) => bs.getIn(['groups', groupId]));
regSub('betslip/groups', upstream, bs => bs.getIn(['groups']));
regSub('betslip/groups/bet', upstream, (bs, [, groupId, selectionId]) =>
    bs.getIn(['groups', groupId, 'picks', selectionId])
);
regSub('betslip.isPointsStake', upstream, bs => bs.getIn(['isPointsStake']));
regSub('betslip.isPointsStake', upstream, bs => bs.getIn(['isPointsStake']));
regSub('betslip/groups/selectionByBetId', upstream, (bs, [, groupId, betId]) => {
    const selectionId = bs.getIn(['groups', groupId, 'betId2SelectionId', betId]);
    return bs.getIn(['groups', groupId, 'picks', selectionId]);
});
regSub('betslip/isBetSelected', (db, [, betId, tip]) =>
    db.getIn(['server', 'currentPickIds'], List()).contains(betId + '-' + tip)
);
regSub('betslip/isBookmakerApproving', upstream, bs => bs.getIn(['submit', 'bookmakerApproving'], false));
regSub('betslip/isExtraAvailable', upstream, bs => {
    return bs.getIn(['extrasAvailable', 'FreeTicket'], Map()).size || !!bs.getIn(['extraAdvantageInterval']);
});
regSub('betslip/isFreeTicket', upstream, bs => bs.getIn(['extra', 'type']) === 'free');
regSub('betslip/isInitializing', db => !db.getIn(['server']));
regSub('betslip/isLiveTicket', upstream, bs => bs.getIn(['gameType']) === 'Live');
regSub('betslip/isOpen', db => db.getIn(['betslip', 'open']));
export const isBetslipOpen$ = pathSubscription(['betslip', 'open']);
regSub('betslip/isPlaceButtonIntersecting', db => db.getIn(['betslip', 'intersectingPlaceButton']));
regSub('betslip/isPrematch', upstream, bs => bs.getIn(['gameType']) === 'Prematch');
export const isSelectionSelected$$ = (betId, selectionCode) =>
    subscription(
        currentPickIds =>
            // picky z betslipu su bez prefixu, ale ticketus pri createDraft commande vracia prefix l, taktiez posiela selection code ako char nie jeho ascii code
            // to asi nemozeme menit v ticketuse teraz - cize potrebujeme takyto "prepis"
            currentPickIds?.contains(`${betId}-${selectionCode}`),
        pathSubscription(['server', 'currentPickIds'], List())
    );
regSub('betslip/isSubmitted', upstream, bs => bs.getIn(['submit', 'submitted'], false));
regSub('betslip/isSubmittedWithChangedOdds', upstream, bs => bs.getIn(['submit', 'submittedWithChangedOdds'], false));
regSub('betslip/isSystemBetslip', upstream, bs => bs.getIn(['type']) === BETSLIP_TYPES.system);
regSub('betslip/isUserCommandInProgress', db => db.getIn(['betslip', 'command', 'isUserInProgress']));
regSub('betslip/isVirtualTicket', upstream, bs => bs.getIn(['gameType']) === 'Virtual');
regSub('betslip/keepBets', db => db.getIn(['betslip', 'keepBets'], false));
regSub('betslip/lastCmdError', db => {
    let ret = db.getIn(['serverError']);
    if (!ret) {
        const errors = db.getIn(['server', 'betslip', 'errors'], List());
        ret = errors.size ? errors.get(0) : ret;
    }
    return ret;
});
regSub('betslip/pickGroups', upstream, bs =>
    bs
        .getIn(['groups'], Map())
        .keySeq()
        .sortBy(groupId => groupOrder[groupId])
        .toList()
);
regSub('betslip/pickGroupsCount', upstream, bs => bs.getIn(['pickGroupsCount']));
regSub('betslip/picksCount', upstream, bs => bs.getIn(['betCount']));
regSub('betslip/publishTicketHash', upstream, bs => bs.getIn(['submit', 'publishTicketHash']));
regSub('betslip/quickBets', upstream, bs => bs.getIn(['quickBets']));
regSub('betslip/quickbet', db => db.getIn(['betslip', 'quickbet'], false));
regSub(
    'betslip/quickbet/triggerability',
    () => [
        subscribe(['betslip/isSystemBetslip']),
        subscribe(['betslip/showSubmitGrid']),
        subscribe(['betslip/lastCmdError']),
        accountActionAllowed$('PLACE_BETSLIP')
    ],
    ([system, showSubmitGrid, cmdError, amlBetslipSubmitEnabled]) => {
        /* tato logika je skopirovana z betslip footer, treba to este - najma to s tym gridom? */
        const betslipError = cmdError && !cmdError.get('submitError') && !isScoreboardError(cmdError.get('type'));
        const betslipWarn = showSubmitGrid || betslipError;
        return {
            disabled: system || betslipWarn || !amlBetslipSubmitEnabled,
            betslipWarn
        };
    }
);
regSub(
    'betslip/quickbet/visible',
    () => [subscribe(['betslip']), subscribe(['server/betslip']), subscribe(['betslip/quickbet/triggerability'])],
    ([betslip, serverBetslip, betslipTriggerability]) =>
        betslip.get('quickbet') &&
        !betslip.get('open') &&
        !serverBetslip.get('pickIds', List()).isEmpty() &&
        !betslipTriggerability.disabled
);
regSub(
    'betslip/popup/shouldShowGuard',
    () => [subscribe(['server/betslip']), subscribe(['betslip/lastCmdError'])],
    ([bs, lastCmdError]) => {
        return (
            bs.getIn(['submit']) ||
            bs.getIn(['savedBetslipParams']) ||
            lastCmdError ||
            bs.getIn(['errors'], List()).size
        );
    }
);
regSub('betslip/savedBetslipParams', upstream, bs => bs.getIn(['savedBetslipParams']));
regSub('betslip/showCombinations', upstream, bs => bs.getIn(['showCombinations'], false));
regSub('betslip/showMyTickets', db => db.getIn(['route']) !== HOME);
regSub('betslip/showSubmitGrid', upstream, bs => bs.getIn(['submit', 'showGrid'], false));
regSub('betslip/showSubmitLoader', upstream, bs => bs.getIn(['submit', 'showSubmitLoader'], false));
regSub('betslip/stakeOn', upstream, bs => bs.getIn(['stake', 'stakeOn']));
regSub('betslip/stake1', upstream, (bs, [, type]) => bs.getIn(['stake', 'stakes', type]));
regSub('betslip/stakeTypes', upstream, bs =>
    bs
        .getIn(['stake', 'stakes'], Map())
        .keySeq()
        .sortBy(stake => stakeOrder[stake])
        .toList()
);
regSub(
    'betslip/stake/money',
    upstream,
    bs => bs.getIn(['totals', 'spent', 'bodPoints']) || bs.getIn(['stake', 'money'])
);
regSub(
    'betslip/submittedWithKeptBets',
    db =>
        db.getIn(['server', 'betslip', 'gameType']) !== 'Live' &&
        db.getIn(['betslip', 'keepBets'], false) &&
        db.getIn(['server', 'betslip', 'submit', 'submitted'], false)
);
regSub('betslip/systemStake/money', upstream, (bs, [, type]) => bs.getIn(['stake', 'stakes', type, 'money']));
regSub('betslip/totalOdds', upstream, bs => bs.getIn(['totals', 'odds']));
regSub('betslip/totalSpent', upstream, bs => {
    if (bs.getIn(['extra', 'type']) === 'free') {
        const extra = bs.getIn(['extra']);
        return extra ? fromJS({ money: extra.get('amount'), currency: extra.get('currency') }) : null;
    }
    return bs.getIn(['totals', 'spent']);
});
regSub(
    'betslip/spentBodPoints',
    () => [subscribe(['betslip/totalSpent'])],
    ([spent]) => {
        return spent ? spent.get('bodPoints') : 0;
    }
);

regSub('betslip/stakeCurrency', upstream, bs =>
    bs.getIn(['isPointsStake']) ? STAKE_CURRENCY.NIK : STAKE_CURRENCY.EUR
);
regSub('betslip/totalWin', upstream, bs => bs.getIn(['totals', 'win']));
regSub('betslip/totalWin/bodPoints', upstream, bs => bs.getIn(['totals', 'win', 'bodPoints']));
